import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import { useDispatch, useSelector } from "app/store";
import { Container } from "components/layout/Container";
import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomForm from "components/form/CustomForm";
import { GrapeAntdForm, useGrapeAntdForm } from "widgets/form";
import { RegisterFormValues } from "../models";
import { Rule } from "antd/lib/form";
import { hashSHA256 } from "utils/crypto";
import useRegister from "./useRegister";
import PasswordFormItem from "../components/PasswordFormItem";
import ConfirmPasswordFormItem from "../components/ConfirmPasswordFormItem";
import BrandImage from "components/ui/BrandImage";
import styles from "./Register.module.scss";
import useConfigurationQuery from "hooks/queries/useConfigurationQuery";

const Register: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formUtils = useGrapeAntdForm<RegisterFormValues>();
  const { checkInvitation, register } = useSelector(
    (state) => state.loading.effects.auth
  );
  const { checkInvitationData, passwordComplexity, passwordComplexityLoading } =
    useRegister({ userId });
  const configQuery = useConfigurationQuery();

  const handleFinish = useCallback(
    async (values: RegisterFormValues): Promise<void> => {
      await dispatch.auth.register({
        userName: values.userName,
        passwordHash: await hashSHA256(values.password),
      });
    },
    [dispatch.auth]
  );

  useEffect(() => {
    if (checkInvitationData?.userName) {
      formUtils.form.setFieldsValue({
        userName: checkInvitationData.userName,
      });
    }

    if (checkInvitationData?.orgName) {
      formUtils.form.setFieldsValue({
        companyName: checkInvitationData.orgName,
      });
    }
  }, [
    checkInvitationData?.orgName,
    checkInvitationData?.userName,
    formUtils.form,
  ]);

  useEffect(() => {
    dispatch.auth.logout();
  }, [dispatch.auth]);

  useEffect(() => {
    if (checkInvitation.error) {
      navigate("failed", { replace: true });
    }

    if (register.success) {
      navigate("success", { replace: true });
    }
  }, [checkInvitation.error, navigate, register.success]);

  return (
    <Container>
      <Row justify="center">
        <Col xxl={12} xl={15} lg={18} span={24}>
          <Spin spinning={passwordComplexityLoading}>
            <Card>
              <Space direction="vertical" size="large">
                <Space className={styles.logoAndAppNameContainer}>
                  <BrandImage
                    subPath="fortana-logo.svg"
                    className={styles.logo}
                  />
                  <div className={styles.appName}>
                    {configQuery.data?.applicationName}
                  </div>
                </Space>

                <Typography.Title>{t("register.title")}</Typography.Title>

                <CustomForm formUtils={formUtils} onFinish={handleFinish}>
                  <Space direction="vertical" size="large">
                    <Row justify="center" gutter={[28, 16]}>
                      <Col span={12}>
                        <GrapeAntdForm.Item
                          name="userName"
                          label={t("register.email")}
                        >
                          <Input disabled size="large" />
                        </GrapeAntdForm.Item>
                      </Col>
                      <Col span={12}>
                        <GrapeAntdForm.Item
                          name="companyName"
                          label={t("register.company")}
                        >
                          <Input disabled size="large" />
                        </GrapeAntdForm.Item>
                      </Col>
                      <Col span={12}>
                        <PasswordFormItem
                          passwordComplexity={passwordComplexity}
                          passwordComplexityLoading={passwordComplexityLoading}
                        />
                      </Col>
                      <Col span={12}>
                        <ConfirmPasswordFormItem />
                      </Col>
                      <Col span={24}>
                        <GrapeAntdForm.Item
                          name="agreement"
                          valuePropName="checked"
                          required
                          rules={[
                            {
                              validator: (_: Rule, value: boolean) =>
                                value
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error(t("error.common.needToAccept"))
                                    ),
                            },
                          ]}
                        >
                          <Checkbox>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t("register.acceptTerms", {
                                  link: process.env.REACT_APP_TERMS_LINK,
                                }),
                              }}
                            />
                          </Checkbox>
                        </GrapeAntdForm.Item>
                      </Col>
                    </Row>

                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      loading={register.loading}
                    >
                      {t("register.registerButton")}
                    </Button>
                  </Space>
                </CustomForm>
              </Space>
            </Card>
          </Spin>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
